import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";
import DeleteOutline from "@material-ui/icons/DeleteOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { contrastText } from "../../helpers/contrastText";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  section: {
    margin: theme.spacing(2, 0, 1),
    ...theme.typography.sectionLabel,
  },
  messageRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: theme.spacing(2),
  },
  eligible: {
    margin: theme.spacing(1, 0, 2),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.borderRadius.small,
    backgroundColor: theme.palette.tintedBackground,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

const emptyCampaign = {
  name: "",
  messages: [""],
  targetType: "all",
  tags: [],
  contacts: [],
  scheduledAt: "",
  minDelay: 30,
  maxDelay: 90,
  dailyLimit: 150,
  hourStart: 8,
  hourEnd: 20,
};

const CampaignModal = ({ open, onClose, campaignId }) => {
  const classes = useStyles();
  const [form, setForm] = useState(emptyCampaign);
  const [allTags, setAllTags] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [eligible, setEligible] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data: tags } = await api.get("/tags");
        setAllTags(tags);
        if (!campaignId) {
          setForm(emptyCampaign);
          return;
        }
        const { data } = await api.get(`/campaigns/${campaignId}`);
        const contacts = await Promise.all(
          data.contactIds.map((id) =>
            api.get(`/contacts/${id}`).then((r) => r.data).catch(() => null)
          )
        );
        setForm({
          name: data.name,
          messages: data.messages.length ? data.messages : [""],
          targetType: data.targetType,
          tags: tags.filter((t) => data.tagIds.includes(t.id)),
          contacts: contacts.filter(Boolean),
          scheduledAt: data.scheduledAt
            ? format(parseISO(data.scheduledAt), INPUT_FORMAT)
            : "",
          minDelay: data.minDelay,
          maxDelay: data.maxDelay,
          dailyLimit: data.dailyLimit,
          hourStart: data.hourStart,
          hourEnd: data.hourEnd,
        });
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open, campaignId]);

  useEffect(() => {
    if (!open || form.targetType !== "contacts") return undefined;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: contactSearch, pageNumber: 1 },
        });
        setContactOptions(data.contacts.filter((c) => !c.isGroup));
      } catch (err) {
        toastError(err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [open, contactSearch, form.targetType]);

  // Prévia de quantos contatos elegíveis a campanha alcança
  useEffect(() => {
    if (!open) return undefined;
    setEligible(null);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/campaigns/preview", {
          targetType: form.targetType,
          tagIds: form.tags.map((t) => t.id),
          contactIds: form.contacts.map((c) => c.id),
        });
        setEligible(data.count);
      } catch (err) {
        setEligible(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [open, form.targetType, form.tags, form.contacts]);

  const setMessage = (index, value) =>
    set("messages")(form.messages.map((m, i) => (i === index ? value : m)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      messages: form.messages.filter((m) => m.trim()),
      targetType: form.targetType,
      tagIds: form.tags.map((t) => t.id),
      contactIds: form.contacts.map((c) => c.id),
      scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
      minDelay: Number(form.minDelay),
      maxDelay: Number(form.maxDelay),
      dailyLimit: Number(form.dailyLimit),
      hourStart: Number(form.hourStart),
      hourEnd: Number(form.hourEnd),
    };
    try {
      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, payload);
      } else {
        await api.post("/campaigns", payload);
      }
      toast.success(i18n.t("campaigns.toasts.saved"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const numberField = (field, min, max) => (
    <TextField
      label={i18n.t(`campaignModal.form.${field}`)}
      type="number"
      value={form[field]}
      onChange={(e) => set(field)(e.target.value)}
      variant="outlined"
      size="small"
      inputProps={{ min, max }}
      required
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {i18n.t(campaignId ? "campaignModal.title.edit" : "campaignModal.title.add")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Alert severity="warning" className={classes.field}>
            {i18n.t("campaignModal.warning")}
          </Alert>
          <TextField
            className={classes.field}
            label={i18n.t("campaignModal.form.name")}
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            variant="outlined"
            fullWidth
            required
            autoFocus
            inputProps={{ maxLength: 120 }}
          />

          {form.messages.map((message, index) => (
            <div key={index} className={classes.messageRow}>
              <TextField
                className={classes.field}
                label={i18n.t("campaignModal.form.message", { n: index + 1 })}
                helperText={
                  index === 0
                    ? i18n.t("campaignModal.form.messageHelp", { name: "{{name}}" })
                    : undefined
                }
                value={message}
                onChange={(e) => setMessage(index, e.target.value)}
                variant="outlined"
                multiline
                minRows={3}
                fullWidth
                required={index === 0}
                inputProps={{ maxLength: 4096 }}
              />
              {index > 0 && (
                <IconButton
                  title={i18n.t("campaignModal.form.removeVariation")}
                  onClick={() =>
                    set("messages")(form.messages.filter((_, i) => i !== index))
                  }
                >
                  <DeleteOutline />
                </IconButton>
              )}
            </div>
          ))}
          {form.messages.length < 3 && (
            <Button
              size="small"
              color="primary"
              onClick={() => set("messages")([...form.messages, ""])}
            >
              {i18n.t("campaignModal.form.addVariation")}
            </Button>
          )}

          <div className={classes.section}>{i18n.t("campaignModal.form.target")}</div>
          <RadioGroup
            value={form.targetType}
            onChange={(e) => set("targetType")(e.target.value)}
          >
            <FormControlLabel
              value="all"
              control={<Radio color="primary" />}
              label={i18n.t("campaignModal.form.targetAll")}
            />
            <FormControlLabel
              value="tags"
              control={<Radio color="primary" />}
              label={i18n.t("campaignModal.form.targetTags")}
            />
            <FormControlLabel
              value="contacts"
              control={<Radio color="primary" />}
              label={i18n.t("campaignModal.form.targetContacts")}
            />
          </RadioGroup>

          {form.targetType === "tags" && (
            <Autocomplete
              multiple
              options={allTags}
              value={form.tags}
              getOptionLabel={(o) => o.name}
              getOptionSelected={(o, v) => o.id === v.id}
              filterSelectedOptions
              onChange={(e, value) => set("tags")(value)}
              renderTags={(value, getTagProps) =>
                value.map((tag, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={tag.id}
                    size="small"
                    label={tag.name}
                    style={{ backgroundColor: tag.color, color: contrastText(tag.color) }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("campaignModal.form.tags")}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          )}

          {form.targetType === "contacts" && (
            <Autocomplete
              multiple
              options={contactOptions}
              value={form.contacts}
              getOptionLabel={(o) => (o.number ? `${o.name} (${o.number})` : o.name)}
              getOptionSelected={(o, v) => o.id === v.id}
              filterOptions={(x) => x}
              filterSelectedOptions
              onChange={(e, value) => set("contacts")(value)}
              onInputChange={(e, value, reason) => {
                if (reason === "input") setContactSearch(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("campaignModal.form.contacts")}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          )}

          <div className={classes.eligible}>
            <Typography variant="body2">
              {eligible === null ? (
                <CircularProgress size={14} />
              ) : (
                <strong>{i18n.t("campaignModal.form.eligible", { count: eligible })}</strong>
              )}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {i18n.t("campaignModal.form.eligibleHelp")}
            </Typography>
          </div>

          <TextField
            className={classes.field}
            label={i18n.t("campaignModal.form.scheduledAt")}
            helperText={i18n.t("campaignModal.form.scheduledHelp")}
            type="datetime-local"
            value={form.scheduledAt}
            onChange={(e) => set("scheduledAt")(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <div className={classes.section}>{i18n.t("campaignModal.form.safety")}</div>
          <div className={classes.grid}>
            {numberField("minDelay", 20, 600)}
            {numberField("maxDelay", 20, 900)}
            {numberField("dailyLimit", 1, 1000)}
            {numberField("hourStart", 0, 23)}
            {numberField("hourEnd", 1, 24)}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving} variant="outlined">
            {i18n.t("campaignModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={saving}
            className={classes.btnWrapper}
          >
            {i18n.t("campaignModal.buttons.ok")}
            {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CampaignModal;
